// src/components/Boxes/PurpleMan/purpleMan.component.jsx
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./purpleMan.style.scss";
import { useNavigate } from "react-router-dom";
import RNFBox from "../../Universals/RNFBox/RNFBox.component";
import { useDataLayer } from "../../../Contexts/DataLayer.context";
import NotDragable from "../../Universals/DragBox/notDragBox.component";
import {
  getTokenFromLocalStorage,
  getUserFromToken,
  saveAuthUser,
  forceLogoutToLogin
} from "../../../utils/functions.ts";
import {
  DEFAULT_WHITE_BOARD_GRADE_COLORS,
  WHITE_BOARD_LETTER_GRADE_KEYS,
  WHITE_BOARD_NUMERIC_GRADE_KEYS,
  WHITE_BOARD_PREFERENCES_EVENT,
  getReadableTextColor,
  normalizeWhiteBoardPreferences,
} from "../../../utils/whiteBoardPreferences";
import { useMask } from "../../../Contexts/mask.context";
import { version } from "../../../Contexts/variables.jsx";

const DRAFT_BOARD_ENDPOINT = "https://www.fbxchange.com/ReactPHP/draftBoard.php";
const AUTO_SCROLL_STORAGE_KEY = "FBX_ONE_LINER_AUTO_SCROLL";
const AUTO_SCROLL_EVENT = "FBX_ONE_LINER_AUTO_SCROLL_CHANGE";
const SHOW_SCROLLBARS_STORAGE_KEY = "FBX_ONE_LINER_SHOW_SCROLLBARS";
const SHOW_SCROLLBARS_EVENT = "FBX_ONE_LINER_SHOW_SCROLLBARS_CHANGE";
const RESET_WINDOW_POSITION_EVENT = "FBX_RESET_WINDOW_POSITION";
const PURPLE_MAN_ENDPOINT = "https://www.fbxchange.com/ReactPHP/purpleman.php";

const ADMIN_PERMISSION_FIELDS = [
  ["birdDog", "Bird Dog"],
  ["showONLIST", "One Liner"],
  ["showS1", "S1 Search"],
  ["showS2", "S2 Search"],
  ["showS3", "S3"],
  ["showS4", "S4"],
  ["showLIST", "Lists"],
  ["showSHARED", "Shared Lists"],
  ["showEXPORT", "Export"],
  ["showTRANS", "Transactions"],
  ["showHWS", "Hard Data"],
  ["showOprah", "Overview"],
  ["SeeBD2020", "See Bird Dog"],
  ["showHGSNPQ", "HG/SNPQ"],
  ["showREPORT", "Reports"],
  ["showFU", "Files"],
  ["showNOTE", "Notes"],
  ["showOSSFBX", "OSS/FBX"],
  ["showCREATION", "Add/Edit"],
  ["showEditHWS", "Edit HWS"],
  ["SHOW_ALERT", "Alerts"],
];

const ADMIN_SHARE_TEAM_OPTIONS = ["FBX", "PYLON", "CFLHQ", "OSS", "TSA", "DRSCT"];
const ADMIN_LEAGUE_OPTIONS = [
  { value: "CFL", label: "CFL" },
  { value: "NFL", label: "NFL" },
  { value: "UFL24", label: "UFL24" },
  { value: "NCAA", label: "NCAA" },
  { value: "GENERAL", label: "General" },
];

const getAdminLeagueLabel = (leagueValue) => (
  ADMIN_LEAGUE_OPTIONS.find((league) => league.value === leagueValue)?.label || "CFL"
);

const getAdminShareTeamOptions = (ownTeam = "") => {
  const teams = [...ADMIN_SHARE_TEAM_OPTIONS];
  const cleanOwnTeam = String(ownTeam || "").trim();
  if (cleanOwnTeam && !teams.includes(cleanOwnTeam)) {
    teams.push(cleanOwnTeam);
  }

  return teams.map((team) => ({ Team: team, Team_Name: team }));
};

const ADMIN_PASSWORD_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";

const formatCacheSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return "0 KB";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

const generateAdminPassword = () => {
  const randomValues = new Uint32Array(8);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(randomValues);
  } else {
    for (let index = 0; index < randomValues.length; index += 1) {
      randomValues[index] = Math.floor(Math.random() * ADMIN_PASSWORD_CHARS.length);
    }
  }

  return Array.from(randomValues)
    .map((value) => ADMIN_PASSWORD_CHARS[value % ADMIN_PASSWORD_CHARS.length])
    .join("");
};


const MOVABLE_WINDOWS = [
  { label: "S1 Search", storageKey: "S1SEARCH_DRAGBOX_POS_2026" },
  { label: "S2 Search", storageKey: "S2SEARCH_DRAGBOX_POS_2026" },
  { label: "Lists", storageKey: "littleBoxPosition" },
  { label: "Shared Lists", storageKey: "L2_BUTTON_LOCATION" },
  { label: "Saved Query", storageKey: "SQ_BOX_LOCATION" },
  { label: "Alerts", storageKey: "Alert_BOX_LOCATION" },
  { label: "BluBars", storageKey: "FBX_BLUE_TRANSACTION_DRAG", adminOnly: true },
  { label: "Shared Notes", storageKey: "SHARED_NOTE_POSITION" },
  { label: "Overview", storageKey: "OVERVIEW_BOX_LOCATION" },
  { label: "Add/Edit", storageKey: "TOM_BOX_LOCATION", adminOnly: true },
  { label: "Hard Data", storageKey: "HD_BOX_LOCATION" },
];

const PurpleManBox = ({ onClose, onLogout, buttonID }) => {
  const navigate = useNavigate();
  const [formattedExpiration, setFormattedExpiration] = useState("");
  const [theUser, setUserData] = useState({});
  const [newShareArray, changeNewShareArray] = useState([]);
  const [newShareOptionArray, changeNewShareOptionArray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [confirmingTransactions, setConfirmingTransactions] = useState(false);
  const [sendAlertStatus, setSendAlertStatus] = useState("");
  const [pendingAlertCount, setPendingAlertCount] = useState(null);
  const [pendingAlertError, setPendingAlertError] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);
  const [showSection, setShowSection] = useState("");
  const [showWindowResetPanel, setShowWindowResetPanel] = useState(false);
  const [showWhiteBoardPreferences, setShowWhiteBoardPreferences] = useState(false);
  const [whiteBoardPreferences, setWhiteBoardPreferences] = useState(() => normalizeWhiteBoardPreferences());
  const [whiteBoardPreferencesLoading, setWhiteBoardPreferencesLoading] = useState(false);
  const [whiteBoardPreferencesSaving, setWhiteBoardPreferencesSaving] = useState(false);
  const [whiteBoardPreferencesMessage, setWhiteBoardPreferencesMessage] = useState("");
  const [windowResetMessage, setWindowResetMessage] = useState("");
  const [oneLinerAutoScroll, setOneLinerAutoScroll] = useState(() => {
    try {
      return localStorage.getItem(AUTO_SCROLL_STORAGE_KEY) !== "Off";
    } catch {
      return true;
    }
  });
  const [oneLinerShowScrollbars, setOneLinerShowScrollbars] = useState(() => {
    try {
      return localStorage.getItem(SHOW_SCROLLBARS_STORAGE_KEY) === "On";
    } catch {
      return false;
    }
  });
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminLeague, setAdminLeague] = useState("CFL");
  const [adminTeams, setAdminTeams] = useState([]);
  const [expandedAdminTeams, setExpandedAdminTeams] = useState({});
  const [adminPanelLoading, setAdminPanelLoading] = useState(false);
  const [adminPanelError, setAdminPanelError] = useState("");
  const [adminEditUser, setAdminEditUser] = useState(null);
  const [adminEditTab, setAdminEditTab] = useState("hardData");
  const [adminEditForm, setAdminEditForm] = useState({});
  const [adminPermissionForm, setAdminPermissionForm] = useState({});
  const [adminPasswordForm, setAdminPasswordForm] = useState({ password: "" });
  const [adminEditSaving, setAdminEditSaving] = useState(false);
  const [adminEditMessage, setAdminEditMessage] = useState("");
  const [adminCreateTeam, setAdminCreateTeam] = useState("");
  const [adminCreateForm, setAdminCreateForm] = useState({ username: "", email: "", phone: "", password: "" });
  const [adminCreateSaving, setAdminCreateSaving] = useState(false);
  const [adminCreateMessage, setAdminCreateMessage] = useState("");
  const [adminNewTeamOpen, setAdminNewTeamOpen] = useState(false);
  const [adminNewTeamForm, setAdminNewTeamForm] = useState({ teamCode: "", teamName: "" });
  const [adminNewTeamSaving, setAdminNewTeamSaving] = useState(false);
  const [adminNewTeamMessage, setAdminNewTeamMessage] = useState("");
  const [reactCacheSummary, setReactCacheSummary] = useState("Checking...");

  const { arrayOfShared, actualShared } = useDataLayer();
  const { mask, unmask } = useMask();
  const isAdmin = String(theUser?.userContactLevel || "") === "1";

  const loadWhiteBoardPreferences = async (team) => {
    if (!team) return;

    setWhiteBoardPreferencesLoading(true);
    setWhiteBoardPreferencesMessage("");

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getPreferences",
          team,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        setWhiteBoardPreferences(normalizeWhiteBoardPreferences(data.preferences));
      } else {
        setWhiteBoardPreferences(normalizeWhiteBoardPreferences());
        setWhiteBoardPreferencesMessage(data?.message || "Unable to load White Board preferences.");
      }
    } catch {
      setWhiteBoardPreferences(normalizeWhiteBoardPreferences());
      setWhiteBoardPreferencesMessage("Unable to load White Board preferences.");
    } finally {
      setWhiteBoardPreferencesLoading(false);
    }
  };

  const normalizeActiveFlag = (value) => {
    const flag = String(value ?? "").trim().toLowerCase();
    return flag === "yes" || flag === "y" || flag === "1" || flag === "true" || flag === "active" || flag === "on";
  };

  const getAdminTeamSummary = (team) => {
    const users = Array.isArray(team?.users) ? team.users : [];
    const activeCount = users.filter((user) => normalizeActiveFlag(user.isActive)).length;
    return {
      activeCount,
      inactiveCount: users.length - activeCount,
      totalCount: users.length,
    };
  };

  const getAdminUserName = (user) => (
    user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || `User ${user?.userId || ""}`
  );

  const parseAdminTeamCsv = (value) => String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  const formatAdminTeamCsv = (teams, forcedTeam = "") => {
    const nextTeams = [];
    const addTeam = (team) => {
      const cleanTeam = String(team || "").trim();
      if (cleanTeam && !nextTeams.includes(cleanTeam)) {
        nextTeams.push(cleanTeam);
      }
    };

    addTeam(forcedTeam);
    (Array.isArray(teams) ? teams : parseAdminTeamCsv(teams)).forEach(addTeam);

    return nextTeams.join(",");
  };

  const buildAdminPermissionForm = (user) => {
    const userPermissions = user?.permissions && typeof user.permissions === "object" ? user.permissions : {};
    return ADMIN_PERMISSION_FIELDS.reduce((next, [field]) => ({
      ...next,
      [field]: normalizeActiveFlag(userPermissions[field] ?? user?.[field]) ? "Yes" : "No",
    }), {
      isActive: normalizeActiveFlag(user?.isActive) ? "Yes" : "No",
      assignTask: normalizeActiveFlag(user?.assignTask) ? "Yes" : "No",
      cfl: normalizeActiveFlag(user?.cfl) ? "Yes" : "No",
    });
  };

  const openAdminUserEditor = (user) => {
    setAdminEditUser(user);
    setAdminEditTab("hardData");
    setAdminEditForm({
      displayName: user?.displayName || "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phone || "",
      address: user?.address || "",
      city: user?.city || "",
      provState: user?.provState || "",
      postal: user?.postal || "",
      userCountry: user?.userCountry || "",
      role: user?.role || user?.title || "",
      danNote: user?.danNote || user?.notes || "",
      team: user?.team || "",
      newShare: formatAdminTeamCsv(user?.newShare || "", user?.team || ""),
      newShareOption: formatAdminTeamCsv(user?.newShareOption || ""),
    });
    setAdminPermissionForm(buildAdminPermissionForm(user));
    setAdminPasswordForm({ password: generateAdminPassword() });
    setAdminEditMessage("");
  };

  const updateAdminEditField = (field, value) => {
    setAdminEditForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "team" ? { newShare: formatAdminTeamCsv(current.newShare, value) } : {}),
    }));
    setAdminEditMessage("");
  };

  const updateAdminTeamCsvField = (field, team, checked) => {
    setAdminEditForm((current) => {
      const ownTeam = String(current.team || "").trim();
      if (field === "newShare" && team === ownTeam && !checked) {
        return current;
      }

      const selectedTeams = parseAdminTeamCsv(current[field]);
      const nextTeams = checked
        ? [...selectedTeams, team]
        : selectedTeams.filter((selectedTeam) => selectedTeam !== team);

      return {
        ...current,
        [field]: formatAdminTeamCsv(nextTeams, field === "newShare" ? ownTeam : ""),
      };
    });
    setAdminEditMessage("");
  };

  const toggleAdminPermissionField = (field) => {
    if (field === "assignTask") {
      const isCurrentlyAdmin = normalizeActiveFlag(adminPermissionForm[field]);
      const actionLabel = isCurrentlyAdmin ? "remove Admin Control from" : "give Admin Control to";
      const scoutName = getAdminUserName(adminEditUser);
      const confirmed = window.confirm(`Are you sure you want to ${actionLabel} ${scoutName}? This status is really important.`);

      if (!confirmed) {
        return;
      }
    }

    setAdminPermissionForm((current) => ({
      ...current,
      [field]: normalizeActiveFlag(current[field]) ? "No" : "Yes",
    }));
    setAdminEditMessage("");
  };

  const renderAdminPermissionToggle = (field, selectedLabel = "Yes", unselectedLabel = "No") => {
    const isSelected = normalizeActiveFlag(adminPermissionForm[field]);
    return (
      <button
        type="button"
        className={isSelected ? "purpleman-admin-permission-toggle purpleman-admin-permission-toggle--yes" : "purpleman-admin-permission-toggle purpleman-admin-permission-toggle--no"}
        onClick={() => toggleAdminPermissionField(field)}
      >
        {isSelected ? selectedLabel : unselectedLabel}
      </button>
    );
  };

  const closeAdminUserEditor = () => {
    if (adminEditSaving) return;
    setAdminEditUser(null);
    setAdminEditTab("hardData");
    setAdminEditForm({});
    setAdminPermissionForm({});
    setAdminPasswordForm({ password: "" });
    setAdminEditMessage("");
  };

  const replaceAdminUser = (nextUser) => {
    setAdminTeams((teams) => teams.map((team) => {
      const remainingUsers = (team.users || []).filter((user) => String(user.userId) !== String(nextUser.userId));
      const nextUsers = String(team.teamCode) === String(nextUser.team)
        ? [...remainingUsers, nextUser].sort((a, b) => {
            const activeSort = Number(normalizeActiveFlag(b.isActive)) - Number(normalizeActiveFlag(a.isActive));
            if (activeSort !== 0) return activeSort;
            return getAdminUserName(a).localeCompare(getAdminUserName(b));
          })
        : remainingUsers;

      return {
        ...team,
        users: nextUsers,
      };
    }));
  };

  const getAdminTeamOptions = () => adminTeams.map((team) => ({
    Team: team.teamCode,
    Team_Name: team.teamName,
  }));

  const openAdminCreateUser = (teamCode = "") => {
    setAdminCreateTeam(teamCode || adminTeams[0]?.teamCode || "");
    setAdminCreateForm({ username: "", email: "", phone: "", password: generateAdminPassword() });
    setAdminCreateMessage("");
  };

  const closeAdminCreateUser = () => {
    if (adminCreateSaving) return;
    setAdminCreateTeam("");
    setAdminCreateForm({ username: "", email: "", phone: "", password: "" });
    setAdminCreateMessage("");
  };

  const updateAdminCreateField = (field, value) => {
    setAdminCreateForm((current) => ({
      ...current,
      [field]: value,
    }));
    setAdminCreateMessage("");
  };

  const saveNewAdminUser = async () => {
    if (!theUser?.scout || adminCreateSaving) return;

    const username = String(adminCreateForm.username || "").trim();
    const email = String(adminCreateForm.email || "").trim();
    const phone = String(adminCreateForm.phone || "").trim();
    const password = String(adminCreateForm.password || "");
    const team = String(adminCreateTeam || "").trim();
    if (!username || !email || !phone || !password || !team) {
      setAdminCreateMessage("Username, email, phone, password, and team are required.");
      return;
    }
    if (password.length < 8) {
      setAdminCreateMessage("Password must be at least 8 characters.");
      return;
    }

    setAdminCreateSaving(true);
    setAdminCreateMessage("Saving...");

    try {
      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          method: "createCflAdminUser",
          adminUser: theUser.scout,
          username,
          email,
          phone,
          password,
          team,
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success || !data?.user) {
        setAdminCreateMessage(data?.error || "Unable to add user.");
        return;
      }

      replaceAdminUser(data.user);
      setExpandedAdminTeams((current) => ({
        ...current,
        [data.user.team]: true,
      }));
      setAdminCreateTeam("");
      setAdminCreateForm({ username: "", email: "", phone: "", password: generateAdminPassword() });
      setAdminCreateMessage("");
      openAdminUserEditor(data.user);
    } catch {
      setAdminCreateMessage("Unable to add user.");
    } finally {
      setAdminCreateSaving(false);
    }
  };

  const saveAdminUser = async () => {
    if (!adminEditUser?.userId || !theUser?.scout || adminEditSaving) return;

    setAdminEditSaving(true);
    setAdminEditMessage("Saving...");

    try {
      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          method: "updateCflAdminUser",
          adminUser: theUser.scout,
          targetUserId: adminEditUser.userId,
          ...adminEditForm,
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success || !data?.user) {
        setAdminEditMessage(data?.error || "Unable to save user.");
        return;
      }

      replaceAdminUser(data.user);
      setAdminEditUser(data.user);
      setAdminEditForm({
        displayName: data.user?.displayName || "",
        firstName: data.user?.firstName || "",
        lastName: data.user?.lastName || "",
        email: data.user?.email || "",
        phone: data.user?.phone || "",
        address: data.user?.address || "",
        city: data.user?.city || "",
        provState: data.user?.provState || "",
        postal: data.user?.postal || "",
        userCountry: data.user?.userCountry || "",
        role: data.user?.role || data.user?.title || "",
        danNote: data.user?.danNote || data.user?.notes || "",
        team: data.user?.team || "",
        newShare: formatAdminTeamCsv(data.user?.newShare || "", data.user?.team || ""),
        newShareOption: formatAdminTeamCsv(data.user?.newShareOption || ""),
      });
      setAdminPermissionForm(buildAdminPermissionForm(data.user));
      setAdminEditMessage("Saved.");
    } catch {
      setAdminEditMessage("Unable to save user.");
    } finally {
      setAdminEditSaving(false);
    }
  };

  const saveAdminPermissions = async () => {
    if (!adminEditUser?.userId || !theUser?.scout || adminEditSaving) return;

    setAdminEditSaving(true);
    setAdminEditMessage("Saving...");

    try {
      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          method: "updateCflAdminUserPermissions",
          adminUser: theUser.scout,
          targetUserId: adminEditUser.userId,
          ...adminPermissionForm,
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success || !data?.user) {
        setAdminEditMessage(data?.error || "Unable to save permissions.");
        return;
      }

      replaceAdminUser(data.user);
      setAdminEditUser(data.user);
      setAdminPermissionForm(buildAdminPermissionForm(data.user));
      setAdminEditMessage("Saved.");
    } catch {
      setAdminEditMessage("Unable to save permissions.");
    } finally {
      setAdminEditSaving(false);
    }
  };

  const updateAdminPasswordField = (value) => {
    setAdminPasswordForm({ password: value });
    setAdminEditMessage("");
  };

  const saveAdminPassword = async () => {
    if (!adminEditUser?.userId || !theUser?.scout || adminEditSaving) return;

    const password = String(adminPasswordForm.password || "");
    if (password.length < 8) {
      setAdminEditMessage("Password must be at least 8 characters.");
      return;
    }

    const confirmed = window.confirm(`Change password for ${getAdminUserName(adminEditUser)}?`);
    if (!confirmed) return;

    setAdminEditSaving(true);
    setAdminEditMessage("Saving...");

    try {
      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          method: "updateCflAdminUserPassword",
          adminUser: theUser.scout,
          targetUserId: adminEditUser.userId,
          password,
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setAdminEditMessage(data?.error || "Unable to save password.");
        return;
      }

      if (data?.user) {
        replaceAdminUser(data.user);
        setAdminEditUser(data.user);
      }
      setAdminPasswordForm({ password: generateAdminPassword() });
      setAdminEditMessage("Password saved.");
    } catch {
      setAdminEditMessage("Unable to save password.");
    } finally {
      setAdminEditSaving(false);
    }
  };

  const saveAdminEditor = () => {
    if (adminEditTab === "permissions") {
      saveAdminPermissions();
      return;
    }
    if (adminEditTab === "password") {
      saveAdminPassword();
      return;
    }
    saveAdminUser();
  };

  const loadAdminPanel = async (league = adminLeague) => {
    if (!isAdmin || !theUser?.scout) return;

    const cleanLeague = String(league || "CFL").trim();
    setShowAdminPanel(true);
    setAdminPanelLoading(true);
    setAdminPanelError("");
    setAdminEditUser(null);
    setAdminCreateTeam("");
    setAdminNewTeamOpen(false);

    try {
      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          method: "getLeagueAdminUsers",
          user: theUser.scout,
          league: cleanLeague,
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setAdminTeams([]);
        setAdminPanelError(data?.error || "Unable to load admin users.");
        return;
      }

      setAdminLeague(data.leagueKey || cleanLeague);
      setAdminTeams(Array.isArray(data.teams) ? data.teams : []);
      setExpandedAdminTeams({});
    } catch (error) {
      setAdminTeams([]);
      setAdminPanelError("Unable to load admin users.");
      console.warn("PurpleMan admin panel failed:", error);
    } finally {
      setAdminPanelLoading(false);
    }
  };

  const changeAdminLeague = (event) => {
    const nextLeague = event.target.value;
    setAdminLeague(nextLeague);
    setExpandedAdminTeams({});
    loadAdminPanel(nextLeague);
  };

  const openAdminNewTeam = () => {
    setAdminNewTeamOpen(true);
    setAdminNewTeamForm({ teamCode: "", teamName: "" });
    setAdminNewTeamMessage("");
  };

  const closeAdminNewTeam = () => {
    if (adminNewTeamSaving) return;
    setAdminNewTeamOpen(false);
    setAdminNewTeamForm({ teamCode: "", teamName: "" });
    setAdminNewTeamMessage("");
  };

  const updateAdminNewTeamField = (field, value) => {
    setAdminNewTeamForm((current) => ({
      ...current,
      [field]: field === "teamCode" ? String(value || "").trim().toUpperCase() : value,
    }));
    setAdminNewTeamMessage("");
  };

  const saveAdminNewTeam = async () => {
    if (!theUser?.scout || adminNewTeamSaving) return;

    const teamCode = String(adminNewTeamForm.teamCode || "").trim().toUpperCase();
    const teamName = String(adminNewTeamForm.teamName || "").trim();
    if (!teamCode || !teamName) {
      setAdminNewTeamMessage("Team code and team name are required.");
      return;
    }

    setAdminNewTeamSaving(true);
    setAdminNewTeamMessage("Saving...");

    try {
      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          method: "createAdminTeam",
          adminUser: theUser.scout,
          league: adminLeague,
          teamCode,
          teamName,
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success || !data?.team) {
        setAdminNewTeamMessage(data?.error || "Unable to add team.");
        return;
      }

      setAdminLeague(data.leagueKey || adminLeague);
      setAdminTeams((teams) => [...teams, data.team].sort((a, b) => String(a.teamName || "").localeCompare(String(b.teamName || ""))));
      setExpandedAdminTeams((current) => ({
        ...current,
        [data.team.teamCode]: true,
      }));
      setAdminNewTeamForm({ teamCode: "", teamName: "" });
      setAdminNewTeamMessage(`${data.team.teamName} added.`);
    } catch {
      setAdminNewTeamMessage("Unable to add team.");
    } finally {
      setAdminNewTeamSaving(false);
    }
  };

  const toggleAdminTeam = (teamCode) => {
    setExpandedAdminTeams((current) => ({
      ...current,
      [teamCode]: !current?.[teamCode],
    }));
  };

  const updateWhiteBoardGradeColor = (gradeKey, color) => {
    setWhiteBoardPreferences((current) => normalizeWhiteBoardPreferences({
      ...current,
      gradeColors: {
        ...(current?.gradeColors || {}),
        [gradeKey]: color,
      },
    }));
    setWhiteBoardPreferencesMessage("");
  };

  const resetWhiteBoardGradeColors = () => {
    setWhiteBoardPreferences(normalizeWhiteBoardPreferences({
      ...whiteBoardPreferences,
      gradeColors: DEFAULT_WHITE_BOARD_GRADE_COLORS,
    }));
    setWhiteBoardPreferencesMessage("Defaults restored. Save to apply to the team.");
  };

  const saveWhiteBoardPreferences = async () => {
    if (!theUser?.team || !theUser?.scout || whiteBoardPreferencesSaving) return;

    const preferencesToSave = normalizeWhiteBoardPreferences(whiteBoardPreferences);
    setWhiteBoardPreferencesSaving(true);
    setWhiteBoardPreferencesMessage("Saving...");

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "savePreferences",
          team: theUser.team,
          scoutId: theUser.scout,
          scoutName: theUser.displayName || "",
          preferences: preferencesToSave,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        const savedPreferences = normalizeWhiteBoardPreferences(data.preferences || preferencesToSave);
        setWhiteBoardPreferences(savedPreferences);
        try {
          window.dispatchEvent(new CustomEvent(WHITE_BOARD_PREFERENCES_EVENT, {
            detail: { preferences: savedPreferences },
          }));
        } catch {}
        setWhiteBoardPreferencesMessage("White Board preferences saved.");
        setShowWhiteBoardPreferences(false);
      } else {
        setWhiteBoardPreferencesMessage(data?.message || "Unable to save White Board preferences.");
      }
    } catch {
      setWhiteBoardPreferencesMessage("Unable to save White Board preferences.");
    } finally {
      setWhiteBoardPreferencesSaving(false);
    }
  };

  const formatExpiryFromToken = (tokenIn) => {
    try {
      if (!tokenIn?.exp) return "";

      return new Date(tokenIn.exp * 1000)
        .toLocaleString("en-CA", {
          timeZone: "America/Toronto",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
        .replace(",", "");
    } catch (e) {
      console.warn("Could not read token expiry:", e);
      return "";
    }
  };

  const formatAccountDateTime = (dateIn) => {
    try {
      if (!dateIn) return "";

      const normalized = String(dateIn).trim().replace(" ", "T");
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return String(dateIn);

      return date
        .toLocaleString("en-CA", {
          timeZone: "America/Toronto",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
        .replace(",", "");
    } catch {
      return String(dateIn || "");
    }
  };

  useEffect(() => {
    try {
      mask();
    } catch {}

    return () => {
      try {
        unmask();
      } catch {}
    };
  }, [mask, unmask]);

  const handleClose = (event) => {
    try {
      unmask();
    } catch {}

    if (onClose) {
      onClose(event, buttonID);
    }
  };

  const signout = () => {
    try {
      unmask();
    } catch {}

    if (onClose) {
      onClose(null, buttonID);
    }

    if (onLogout) {
      onLogout();
      return;
    }

    forceLogoutToLogin();
  };

  useEffect(() => {
    (async () => {
      try {
        const user = await getUserFromToken();
        console.log(user);

        const cleanUserData = user?.newShare?.endsWith(",")
          ? user.newShare.slice(0, -1)
          : user?.newShare || "";

        const shareArray = cleanUserData ? cleanUserData.split(",") : [];

        const nextShareOptions = user?.newShareOption?.endsWith(",")
          ? user.newShareOption.slice(0, -1).split(",")
          : (user?.newShareOption || "").split(",").filter(Boolean);

        changeNewShareArray(shareArray);
        changeNewShareOptionArray(nextShareOptions);
        setUserData(user || {});
        setFormattedExpiration(formatExpiryFromToken(user));
        loadWhiteBoardPreferences(user?.team);
        setLoading(false);
      } catch (e) {
        console.warn(e);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    let alive = true;

    const loadReactCacheSummary = async () => {
      try {
        if (navigator.storage?.estimate) {
          const estimate = await navigator.storage.estimate();
          if (alive) {
            setReactCacheSummary(`${formatCacheSize(estimate.usage)} used`);
          }
          return;
        }

        if ("caches" in window) {
          const cacheNames = await window.caches.keys();
          let totalBytes = 0;

          for (const cacheName of cacheNames) {
            const cache = await window.caches.open(cacheName);
            const requests = await cache.keys();

            for (const request of requests) {
              const response = await cache.match(request);
              const sizeHeader = response?.headers?.get("content-length");
              if (sizeHeader) {
                totalBytes += Number(sizeHeader) || 0;
              } else if (response?.blob) {
                totalBytes += (await response.blob()).size;
              }
            }
          }

          if (alive) {
            setReactCacheSummary(`${formatCacheSize(totalBytes)} used`);
          }
          return;
        }

        if (alive) {
          setReactCacheSummary("Not available");
        }
      } catch {
        if (alive) {
          setReactCacheSummary("Not available");
        }
      }
    };

    loadReactCacheSummary();

    return () => {
      alive = false;
    };
  }, []);

  const handleToggle = async (title) => {
    const newArray = newShareArray.includes(title)
      ? newShareArray.filter((item) => item !== title)
      : [...newShareArray, title];

    changeNewShareArray(newArray);
  };

  const extendClick = async () => {
    try {
      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          method: "extend",
          user: theUser?.scout,
          theValue: "-",
        }).toString(),
      });

      const data = await response.json();
      console.log("PurpleMan extend response:", data);

      if (data?.user) {
        try {
          const decodedExtendJwt = JSON.parse(
            atob(data.user.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
          );
          console.log("PurpleMan extend decoded JWT:", decodedExtendJwt);
          console.log("PurpleMan extend non-Yes show fields:", decodedExtendJwt.showFields || {});
        } catch (decodeError) {
          console.warn("PurpleMan extend JWT decode failed:", decodeError);
        }
      }

      if (data?.success && data?.user) {
        saveAuthUser(data.user);

        const freshToken = await getTokenFromLocalStorage();
        setFormattedExpiration(formatExpiryFromToken(freshToken));

        const freshUser = await getUserFromToken().catch(() => theUser);
        if (freshUser) {
          setUserData(freshUser);
        }
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const returnNLA = async () => {
    const newValue = theUser?.nla === "On" ? "Off" : "On";

    try {
      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          method: "changeNLA",
          user: theUser?.scout,
          theValue: newValue,
        }).toString(),
      });

      const data = await response.json();

      if (data?.success && data?.user) {
        saveAuthUser(data.user);

        const userData = await getUserFromToken();
        setUserData(userData || {});
        setFormattedExpiration(formatExpiryFromToken(userData));
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const setOpenOnSignIn = async (newValue) => {
    setUserData((s) => ({ ...s, showCalendar: newValue }));

    try {
      const response = await fetch(PURPLE_MAN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          method: "changeCalendar",
          user: theUser?.scout,
          theValue: newValue,
        }).toString(),
      });

      const data = await response.json();

      if (data?.success && data?.user) {
        saveAuthUser(data.user);

        const userData = await getUserFromToken();
        setUserData(userData || {});
        setFormattedExpiration(formatExpiryFromToken(userData));
      }
    } catch (e) {
      console.log(e);
    }
  };

  const toggleOneLinerAutoScroll = () => {
    const nextValue = !oneLinerAutoScroll;
    setOneLinerAutoScroll(nextValue);

    try {
      localStorage.setItem(AUTO_SCROLL_STORAGE_KEY, nextValue ? "On" : "Off");
      window.dispatchEvent(new Event(AUTO_SCROLL_EVENT));
    } catch (e) {
      console.warn("Could not save OneLiner AutoScroll setting:", e);
    }
  };

  const toggleOneLinerScrollbars = () => {
    const nextValue = !oneLinerShowScrollbars;
    setOneLinerShowScrollbars(nextValue);

    try {
      localStorage.setItem(SHOW_SCROLLBARS_STORAGE_KEY, nextValue ? "On" : "Off");
      window.dispatchEvent(new Event(SHOW_SCROLLBARS_EVENT));
    } catch (e) {
      console.warn("Could not save OneLiner scrollbar setting:", e);
    }
  };

  const resetWindowPosition = (windowItem) => {
    try {
      localStorage.removeItem(windowItem.storageKey);
      window.dispatchEvent(
        new CustomEvent(RESET_WINDOW_POSITION_EVENT, {
          detail: { storageKey: windowItem.storageKey },
        })
      );
      setWindowResetMessage(`${windowItem.label} position reset.`);
    } catch (e) {
      setWindowResetMessage(`Could not reset ${windowItem.label}.`);
      console.warn("Could not reset window position:", e);
    }
  };

  const resetAllWindowPositions = () => {
    try {
      visibleMovableWindows.forEach((windowItem) => {
        localStorage.removeItem(windowItem.storageKey);
        window.dispatchEvent(
          new CustomEvent(RESET_WINDOW_POSITION_EVENT, {
            detail: { storageKey: windowItem.storageKey },
          })
        );
      });
      setWindowResetMessage("All window positions reset.");
    } catch (e) {
      setWindowResetMessage("Could not reset all window positions.");
      console.warn("Could not reset all window positions:", e);
    }
  };

  const loadPendingAlertCount = async () => {
    try {
      const response = await fetch("https://www.fbxchange.com/ReactPHP/danSendMail.php?script=countFlush");
      if (!response.ok) {
        setPendingAlertError("count unavailable");
        return;
      }

      const data = await response.json();
      if (data?.success) {
        setPendingAlertCount(Number(data.count || 0));
        setPendingAlertError("");
      } else {
        setPendingAlertError(data?.error || "count unavailable");
      }
    } catch (error) {
      setPendingAlertError("count unavailable");
      console.warn("Could not load pending alert count:", error);
    }
  };

  const sendAlertMessages = async () => {
    if (sendingAlerts) return;

    const confirmed = window.confirm("Send all pending alert messages now?");
    if (!confirmed) return;

    setSendingAlerts(true);
    setSendAlertStatus("Sending alert messages...");

    try {
      const response = await fetch("https://www.fbxchange.com/ReactPHP/danSendMail.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ script: "flushEmailTransactions" }),
      });

      const data = await response.json();
      const sentCount = Array.isArray(data?.sent) ? data.sent.length : 0;
      const failedCount = Array.isArray(data?.failed) ? data.failed.length : 0;

      if (data?.success) {
        setSendAlertStatus(`Sent: ${sentCount} | Failed: ${failedCount}`);
      } else {
        setSendAlertStatus(data?.error || `Alert send finished with ${failedCount} failure(s).`);
      }
      await loadPendingAlertCount();
    } catch (error) {
      setSendAlertStatus("Alert send failed. Check connection or server logs.");
      console.warn("Send alert messages failed:", error);
    } finally {
      setSendingAlerts(false);
    }
  };

  const confirmAlertTransactions = async () => {
    if (confirmingTransactions) return;

    setConfirmingTransactions(true);
    setSendAlertStatus("Refreshing pending alert messages...");

    try {
      await loadPendingAlertCount();
      setSendAlertStatus("Pending alert messages refreshed.");
    } finally {
      setConfirmingTransactions(false);
    }
  };

  const visibleMovableWindows = MOVABLE_WINDOWS.filter((windowItem) => !windowItem.adminOnly || isAdmin);

  useEffect(() => {
    if (isAdmin) {
      loadPendingAlertCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (loading) {
    return <div>Loading...</div>;
  }

  const RNFNote =
    "FBX and other contributing sources add Reports, Notes, and Files to the system. By Default, you only see the reports, notes, and files from your team, but if other sources are available, checking them will allow you to see reports, notes, and files from them as well.";

  const extendNote =
    "Extending your session will give you an additional 4 hours before you are automatically logged out. FBX automatically logs you out after 4 hours for your security.";

  const calendarNote = "Choose what opens automatically on Sign In.";
  const autoScrollNote =
    "When AutoScroll is On, the main OneLiner scrolls to the selected player after sorting or player changes. Notes and Reports one-liners are not affected.";
  const scrollbarsNote =
    "When Show Scrollbars is On, the main, notes, and reports one-liners show a draggable scrollbar on the right side. It is Off by default.";
  const resetWindowsNote =
    "Use Reset Window Positions if a movable window has been dragged off screen. Reset one window or all saved positions.";
  const whiteBoardPreferencesNote =
    "White Board Preferences controls team-wide grade colors shown on White Board player cards and Player Data. Position marker color is set on each board from White Board.";

  const showHelp = (inval) => {
    if (inval === "") {
      setShowInstructions(false);
      setShowSection("");
    } else {
      setShowInstructions(true);
      switch (inval) {
        case "RNF":
          setShowSection(RNFNote);
          break;
        case "NLA":
          setShowSection(
            "Each day when Transactions are added to the System, if you have Receive Neg List alert turned on, the system will automatically send you an email telling you of the new information (status) of the player on your Negotiation List"
          );
          break;
        case "Extend":
          setShowSection(extendNote);
          break;
        case "Calendar":
          setShowSection(calendarNote);
          break;
        case "AutoScroll":
          setShowSection(autoScrollNote);
          break;
        case "Scrollbars":
          setShowSection(scrollbarsNote);
          break;
        case "ResetWindows":
          setShowSection(resetWindowsNote);
          break;
        case "WhiteBoardPreferences":
          setShowSection(whiteBoardPreferencesNote);
          break;
        default:
          setShowSection("");
      }
    }
  };

  const openOnSignInValue = theUser?.showCalendar || "CAL";
  const accountInfoItems = [
    { label: "User", value: theUser?.displayName || "-" },
    { label: "Team", value: theUser?.team || "-" },
    { label: "Security", value: theUser?.userContactLevel || "-" },
    { label: "Times On", value: theUser?.timeOn || "-" },
    { label: "Last On", value: formatAccountDateTime(theUser?.lastOn) || "-" },
    { label: "Last IP", value: theUser?.lastIP || "-" },
    { label: "Auto Log Out", value: formattedExpiration || "N/A" },
    { label: "Last Login App", value: theUser?.lastVersion || version },
  ];

  return (
    <NotDragable
      centerScreen={true}
      title={`Account - ${theUser?.displayName || "PurpleMan"} | ${version}`}
      width={1080}
      addNote="-"
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ flex: 1, overflow: "auto" }}>
          <div className="d-flex gap-3 p-2 fs-7">
            <div className="col-18 bg-purple p-2" style={{ minHeight: 330 }}>
              <div className="text-white text-center fw-bold">Account Panel Help</div>

              {!showInstructions ? (
                <div className="mb-2 d-flex flex-column align-items-center">
                  <div className="mt-2 col-30 offet-9">
                    <div className="btn btn-sm btn-light fs-7 w-100" onClick={() => showHelp("RNF")}>
                      RNF Sources
                    </div>
                  </div>
                  <div className="mt-2 col-30 offet-9">
                    <div className="btn btn-sm btn-light w-100" onClick={() => showHelp("Extend")}>
                      Extend
                    </div>
                  </div>
                  <div className="mt-2 col-30 offet-9">
                    <div className="btn btn-sm btn-light w-100" onClick={() => showHelp("NLA")}>
                      NLA
                    </div>
                  </div>
                  <div className="mt-2 col-30 offet-9">
                    <div className="btn btn-sm btn-light w-100" onClick={() => showHelp("Calendar")}>
                      Open on Sign In
                    </div>
                  </div>
                  <div className="mt-2 col-30 offet-9">
                    <div className="btn btn-sm btn-light w-100" onClick={() => showHelp("AutoScroll")}>
                      AutoScroll
                    </div>
                  </div>
                  <div className="mt-2 col-30 offet-9">
                    <div className="btn btn-sm btn-light w-100" onClick={() => showHelp("Scrollbars")}>
                      Scrollbars
                    </div>
                  </div>
                  <div className="mt-2 col-30 offet-9">
                    <div className="btn btn-sm btn-light w-100" onClick={() => showHelp("ResetWindows")}>
                      Window Reset
                    </div>
                  </div>
                  <div className="mt-2 col-30 offet-9">
                    <div className="btn btn-sm btn-light w-100" onClick={() => showHelp("WhiteBoardPreferences")}>
                      WB Preferences
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-white p-3">
                  <p className="p-2 mt-2">
                    <div className="text-center">{showSection}</div>
                  </p>
                  <div className="col-24 offset-12 mt-auto">
                    <div className="btn btn-sm btn-light w-100" onClick={() => showHelp("")}>
                      OK
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-grow-1 p-2">
              <div className="purpleman-cache-banner mb-2">
                <span>Cache</span>
                <strong>{reactCacheSummary}</strong>
              </div>

              <div className="purpleman-account-summary mb-2">
                {accountInfoItems.map((item) => (
                  <div className="purpleman-account-summary__item" key={item.label}>
                    <div className="purpleman-account-summary__label">{item.label}</div>
                    <div className="purpleman-account-summary__value" title={String(item.value || "")}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              <div className="d-flex gap-2 align-items-center purpleman-control-row">
                <div className="col-12 text-end">RNF Sources:</div>
                <div className="flex-grow-1 text-start">
                  <RNFBox buttonList={arrayOfShared} selectedButtons={actualShared} helpbutton={false} />
                </div>
              </div>

              <div className="d-flex gap-2 purpleman-control-row">
                <div className="col-12 text-end">
                  <div className="btn btn-sm btn-outline-purple w-100" onClick={extendClick}>
                    Extend
                  </div>
                </div>
                <div className="flex-grow-1 text-start">Auto Log Out: {formattedExpiration || "N/A"}</div>
              </div>

              <div className="d-flex gap-2 purpleman-control-row">
                <div className="col-12 text-end">
                  <div
                    className={`btn btn-sm ${theUser?.nla === "On" ? "btn-outline-success" : "btn-outline-danger"} w-100`}
                    onClick={returnNLA}
                  >
                    NLA
                  </div>
                </div>
                <div className={`${theUser?.nla === "On" ? "text-success fw-bold" : "text-danger"}`}>
                  Receive Neg List Alerts : {theUser?.nla}
                </div>
              </div>

              <div className="d-flex gap-2 align-items-center purpleman-control-row">
                <div className="col-12 text-end">
                  Open on Sign In:
                </div>
                <div className="flex-grow-1 text-start">
                  <select
                    className="form-select form-select-sm"
                    value={openOnSignInValue}
                    onChange={(e) => setOpenOnSignIn(e.target.value)}
                  >
                    <option value="-">Show Nothing</option>
                    <option value="CAL">Calendar</option>
                    <option value="S1">S1 Search</option>
                    <option value="S2">S2 Search</option>
                    <option value="L1">Lists</option>
                    <option value="L2">Shared Lists</option>
                    <option value="SQ">Saved Query</option>
                    <option value="SN">Shared Note</option>
                  </select>
                </div>
              </div>

              <div className="d-flex gap-2 purpleman-control-row">
                <div className="col-12 text-end">
                  <div
                    className={`btn btn-sm ${oneLinerAutoScroll ? "btn-outline-success" : "btn-outline-danger"} w-100`}
                    onClick={toggleOneLinerAutoScroll}
                  >
                    AutoScroll
                  </div>
                </div>
                <div className={`${oneLinerAutoScroll ? "text-success fw-bold" : "text-danger"}`}>
                  OneLiner AutoScroll : {oneLinerAutoScroll ? "On" : "Off"}
                </div>
              </div>

              <div className="d-flex gap-2 purpleman-control-row">
                <div className="col-12 text-end">
                  <div
                    className={`btn btn-sm ${oneLinerShowScrollbars ? "btn-outline-success" : "btn-outline-danger"} w-100`}
                    onClick={toggleOneLinerScrollbars}
                  >
                    Scrollbars
                  </div>
                </div>
                <div className={`${oneLinerShowScrollbars ? "text-success fw-bold" : "text-danger"}`}>
                  OneLiner Scrollbars : {oneLinerShowScrollbars ? "On" : "Off"}
                </div>
              </div>

              <div className="d-flex gap-2 purpleman-control-row">
                <div className="col-12 text-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-purple w-100"
                    onClick={() => {
                      setShowWindowResetPanel((prev) => !prev);
                      setWindowResetMessage("");
                    }}
                  >
                    Reset Windows
                  </button>
                </div>
                <div className="flex-grow-1 text-start">
                  Window Positions
                </div>
              </div>

              {showWindowResetPanel && (
                <div className="border rounded p-2 mb-2 bg-light">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="fw-bold flex-grow-1 text-start fs-8">Reset Window Positions</div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={resetAllWindowPositions}
                    >
                      Reset All
                    </button>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    {visibleMovableWindows.map((windowItem) => (
                      <button
                        type="button"
                        key={windowItem.storageKey}
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => resetWindowPosition(windowItem)}
                      >
                        {windowItem.label}
                      </button>
                    ))}
                  </div>

                  {windowResetMessage && (
                    <div className="mt-2 text-success fw-bold fs-8 text-start">{windowResetMessage}</div>
                  )}
                </div>
              )}

              {isAdmin && (
                <div className="d-flex gap-2 purpleman-control-row">
                  <div className="col-12 text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary w-100"
                      onClick={() => loadAdminPanel()}
                    >
                      Admin
                    </button>
                  </div>
                  <div className="flex-grow-1 text-start">
                    {getAdminLeagueLabel(adminLeague)} Teams and Users
                  </div>
                </div>
              )}

              {showAdminPanel && typeof document !== "undefined" && createPortal((
                <div className="purpleman-admin-panel-backdrop">
                  <div className="purpleman-admin-panel" role="dialog" aria-modal="true" aria-labelledby="purpleman-admin-panel-title">
                    <div className="purpleman-admin-panel__header">
                      <div>
                        <strong id="purpleman-admin-panel-title">Admin</strong>
                        <span>{getAdminLeagueLabel(adminLeague)} Teams and Users</span>
                      </div>
                      <div className="purpleman-admin-panel__actions">
                        <label className="purpleman-admin-league-control">
                          <span>League</span>
                          <select value={adminLeague} onChange={changeAdminLeague} disabled={adminPanelLoading}>
                            {ADMIN_LEAGUE_OPTIONS.map((league) => (
                              <option value={league.value} key={league.value}>{league.label}</option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={openAdminNewTeam}
                        >
                          New Team
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => openAdminCreateUser()}
                          disabled={adminTeams.length === 0}
                        >
                          New User
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setShowAdminPanel(false)}
                        >
                          Close
                        </button>
                      </div>
                    </div>

                    <div className="purpleman-admin-panel__body">
                      {adminPanelLoading && <div className="purpleman-admin-panel__empty">Loading...</div>}
                      {!adminPanelLoading && adminPanelError && (
                        <div className="purpleman-admin-panel__empty text-danger">{adminPanelError}</div>
                      )}
                      {!adminPanelLoading && !adminPanelError && (
                        <div className="purpleman-admin-team-list">
                          <div className="purpleman-admin-row purpleman-admin-row--header">
                            <div>Team</div>
                            <div className="purpleman-admin-counts purpleman-admin-counts--header">
                              <span>Active</span>
                              <span>Non Active</span>
                              <span>Total</span>
                            </div>
                            <div>Additional Information</div>
                          </div>

                          {adminTeams.map((team) => {
                            const summary = getAdminTeamSummary(team);
                            const isExpanded = !!expandedAdminTeams[team.teamCode];

                            return (
                              <div className="purpleman-admin-team" key={team.teamCode}>
                                <button
                                  type="button"
                                  className={`purpleman-admin-row purpleman-admin-team-button ${isExpanded ? "purpleman-admin-team-button--open" : ""}`}
                                  onClick={() => toggleAdminTeam(team.teamCode)}
                                >
                                  <div className="purpleman-admin-team-name">
                                    <span>{isExpanded ? "-" : "+"}</span>
                                    <strong>{team.teamName}</strong>
                                    <em>{team.teamCode}</em>
                                  </div>
                                  <div className="purpleman-admin-counts">
                                    <span className="purpleman-admin-counts__active">{summary.activeCount}</span>
                                    <span className="purpleman-admin-counts__inactive">{summary.inactiveCount}</span>
                                    <span className="purpleman-admin-counts__total">{summary.totalCount}</span>
                                  </div>
                                  <div className="purpleman-admin-info-space" />
                                </button>

                                {isExpanded && (
                                  <div className="purpleman-admin-user-list">
                                    {team.users.length === 0 ? (
                                      <div className="purpleman-admin-row purpleman-admin-user-row purpleman-admin-user-row--empty">
                                        <div>No users</div>
                                        <div />
                                        <div />
                                      </div>
                                    ) : (
                                      team.users.map((user) => {
                                        const userIsActive = normalizeActiveFlag(user.isActive);
                                        const userHasAdminControl = normalizeActiveFlag(user.assignTask);
                                        const userName = user.displayName || [user.firstName, user.lastName].filter(Boolean).join(" ") || `User ${user.userId}`;

                                        return (
                                          <button
                                            type="button"
                                            className={`purpleman-admin-row purpleman-admin-user-row ${userIsActive ? "purpleman-admin-user-row--active" : "purpleman-admin-user-row--inactive"} ${userHasAdminControl ? "purpleman-admin-user-row--admin-control" : ""}`}
                                            key={`${team.teamCode}-${user.userId}`}
                                            onClick={() => openAdminUserEditor(user)}
                                          >
                                            <div className="purpleman-admin-user-name">
                                              <strong>{userName}</strong>
                                              {userHasAdminControl && <span className="purpleman-admin-control-badge">Admin Control</span>}
                                              <span>{user.email || "-"}</span>
                                            </div>
                                            <div className="purpleman-admin-user-status">
                                              {userIsActive ? "Active" : "Non Active"}
                                            </div>
                                            <div className="purpleman-admin-user-extra">
                                              <span>Security {user.security || "-"}</span>
                                              <span>Last On {formatAccountDateTime(user.lastOn) || "-"}</span>
                                              <span>Times {user.timeOn || "0"}</span>
                                            </div>
                                          </button>
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {adminEditUser && (
                        <div className="purpleman-admin-edit-backdrop">
                          <div className="purpleman-admin-edit-panel" role="dialog" aria-modal="true" aria-labelledby="purpleman-admin-edit-title">
                            <div className="purpleman-admin-edit-header">
                              <div>
                                <strong id="purpleman-admin-edit-title">{getAdminUserName(adminEditUser)}</strong>
                                <span>User {adminEditUser.userId} | Last On {formatAccountDateTime(adminEditUser.lastOn) || "-"} | Times On {adminEditUser.timeOn || "0"}</span>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={closeAdminUserEditor}
                                disabled={adminEditSaving}
                              >
                                Close
                              </button>
                            </div>

                            <div className="purpleman-admin-edit-tabs">
                              <button
                                type="button"
                                className={adminEditTab === "hardData" ? "purpleman-admin-edit-tab purpleman-admin-edit-tab--active" : "purpleman-admin-edit-tab"}
                                onClick={() => {
                                  setAdminEditTab("hardData");
                                  setAdminEditMessage("");
                                }}
                              >
                                Hard Data
                              </button>
                              <button
                                type="button"
                                className={adminEditTab === "permissions" ? "purpleman-admin-edit-tab purpleman-admin-edit-tab--active" : "purpleman-admin-edit-tab"}
                                onClick={() => {
                                  setAdminEditTab("permissions");
                                  setAdminEditMessage("");
                                }}
                              >
                                Permissions
                              </button>
                              <button
                                type="button"
                                className={adminEditTab === "password" ? "purpleman-admin-edit-tab purpleman-admin-edit-tab--active" : "purpleman-admin-edit-tab"}
                                onClick={() => {
                                  setAdminEditTab("password");
                                  setAdminEditMessage("");
                                }}
                              >
                                Password
                              </button>
                            </div>

                            {adminEditTab === "hardData" && (
                              <div className="purpleman-admin-edit-grid">
                              <label>
                                <span>Display Name</span>
                                <input value={adminEditForm.displayName || ""} onChange={(event) => updateAdminEditField("displayName", event.target.value)} />
                              </label>
                              <label>
                                <span>First Name</span>
                                <input value={adminEditForm.firstName || ""} onChange={(event) => updateAdminEditField("firstName", event.target.value)} />
                              </label>
                              <label>
                                <span>Last Name</span>
                                <input value={adminEditForm.lastName || ""} onChange={(event) => updateAdminEditField("lastName", event.target.value)} />
                              </label>
                              <label>
                                <span>Email</span>
                                <input type="email" value={adminEditForm.email || ""} onChange={(event) => updateAdminEditField("email", event.target.value)} />
                              </label>
                              <label>
                                <span>Phone</span>
                                <input value={adminEditForm.phone || ""} onChange={(event) => updateAdminEditField("phone", event.target.value)} />
                              </label>
                              <label>
                                <span>Role</span>
                                <input value={adminEditForm.role || ""} onChange={(event) => updateAdminEditField("role", event.target.value)} />
                              </label>
                              <label>
                                <span>Address</span>
                                <input value={adminEditForm.address || ""} onChange={(event) => updateAdminEditField("address", event.target.value)} />
                              </label>
                              <label>
                                <span>City</span>
                                <input value={adminEditForm.city || ""} onChange={(event) => updateAdminEditField("city", event.target.value)} />
                              </label>
                              <label>
                                <span>Prov/State</span>
                                <input value={adminEditForm.provState || ""} onChange={(event) => updateAdminEditField("provState", event.target.value)} />
                              </label>
                              <label>
                                <span>Postal Code</span>
                                <input value={adminEditForm.postal || ""} onChange={(event) => updateAdminEditField("postal", event.target.value)} />
                              </label>
                              <label>
                                <span>Country</span>
                                <input value={adminEditForm.userCountry || ""} onChange={(event) => updateAdminEditField("userCountry", event.target.value)} />
                              </label>
                              <label>
                                <span>Team</span>
                                <select value={adminEditForm.team || ""} onChange={(event) => updateAdminEditField("team", event.target.value)}>
                                  {getAdminTeamOptions().map((teamOption) => (
                                    <option value={teamOption.Team} key={teamOption.Team}>{teamOption.Team_Name}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="purpleman-admin-notes-field">
                                <span>Note</span>
                                <textarea value={adminEditForm.danNote || ""} onChange={(event) => updateAdminEditField("danNote", event.target.value)} />
                              </label>
                              <div className="purpleman-admin-share-field purpleman-admin-share-field--wide">
                                <span>Visible Options</span>
                                <div className="purpleman-admin-share-options">
                                  {getAdminShareTeamOptions(adminEditForm.team).map((teamOption) => {
                                    const ownTeam = String(adminEditForm.team || "").trim();
                                    const isOwnTeam = teamOption.Team === ownTeam;
                                    const selectedTeams = parseAdminTeamCsv(adminEditForm.newShare);
                                    return (
                                      <label
                                        className={isOwnTeam ? "purpleman-admin-share-option purpleman-admin-share-option--locked" : "purpleman-admin-share-option"}
                                        key={`newShare-${teamOption.Team}`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isOwnTeam || selectedTeams.includes(teamOption.Team)}
                                          disabled={isOwnTeam}
                                          onChange={(event) => updateAdminTeamCsvField("newShare", teamOption.Team, event.target.checked)}
                                        />
                                        <span>{teamOption.Team}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="purpleman-admin-share-field purpleman-admin-share-field--wide">
                                <span>SHARE OPTIONS</span>
                                <div className="purpleman-admin-share-options">
                                  {getAdminShareTeamOptions(adminEditForm.team).map((teamOption) => {
                                    const selectedTeams = parseAdminTeamCsv(adminEditForm.newShareOption);
                                    return (
                                      <label className="purpleman-admin-share-option" key={`newShareOption-${teamOption.Team}`}>
                                        <input
                                          type="checkbox"
                                          checked={selectedTeams.includes(teamOption.Team)}
                                          onChange={(event) => updateAdminTeamCsvField("newShareOption", teamOption.Team, event.target.checked)}
                                        />
                                        <span>{teamOption.Team}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                              </div>
                            )}

                            {adminEditTab === "permissions" && (
                              <div className="purpleman-admin-permissions">
                                <div className="purpleman-admin-permission-topline">
                                  <div className="purpleman-admin-permission-field">
                                    <span>Status</span>
                                    {renderAdminPermissionToggle("isActive", "Active", "Not Active")}
                                  </div>
                                  <div className="purpleman-admin-permission-field">
                                    <span>Admin Control</span>
                                    {renderAdminPermissionToggle("assignTask")}
                                  </div>
                                </div>

                                <div className="purpleman-admin-permission-grid">
                                  <div className="purpleman-admin-permission-field">
                                    <span>CFL</span>
                                    {renderAdminPermissionToggle("cfl")}
                                  </div>
                                  {ADMIN_PERMISSION_FIELDS.map(([field, label]) => (
                                    <div className="purpleman-admin-permission-field" key={field}>
                                      <span>{label}</span>
                                      {renderAdminPermissionToggle(field)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {adminEditTab === "password" && (
                              <div className="purpleman-admin-edit-grid">
                                <div className="purpleman-admin-edit-readonly purpleman-admin-share-field--wide">
                                  <span>Current Password</span>
                                  <strong>{adminEditUser?.currentPassword || "-"}</strong>
                                </div>
                                <label className="purpleman-admin-share-field--wide">
                                  <span>New Password</span>
                                  <div className="purpleman-admin-password-row">
                                    <input
                                      type="text"
                                      autoComplete="new-password"
                                      value={adminPasswordForm.password || ""}
                                      onChange={(event) => updateAdminPasswordField(event.target.value)}
                                      onFocus={(event) => event.target.select()}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => updateAdminPasswordField(generateAdminPassword())}
                                      disabled={adminEditSaving}
                                    >
                                      Generate
                                    </button>
                                  </div>
                                </label>
                              </div>
                            )}

                            <div className="purpleman-admin-edit-footer">
                              <span>{adminEditMessage}</span>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={saveAdminEditor}
                                disabled={adminEditSaving}
                              >
                                {adminEditSaving ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {adminNewTeamOpen && (
                        <div className="purpleman-admin-edit-backdrop">
                          <div className="purpleman-admin-new-team-panel" role="dialog" aria-modal="true" aria-labelledby="purpleman-admin-new-team-title">
                            <div className="purpleman-admin-edit-header">
                              <div>
                                <strong id="purpleman-admin-new-team-title">New Team</strong>
                                <span>{getAdminLeagueLabel(adminLeague)}</span>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={closeAdminNewTeam}
                                disabled={adminNewTeamSaving}
                              >
                                Close
                              </button>
                            </div>

                            <div className="purpleman-admin-edit-grid">
                              <label>
                                <span>Team Code</span>
                                <input
                                  value={adminNewTeamForm.teamCode || ""}
                                  onChange={(event) => updateAdminNewTeamField("teamCode", event.target.value)}
                                  maxLength={20}
                                  placeholder="ABC"
                                />
                              </label>
                              <label>
                                <span>Team Name</span>
                                <input
                                  value={adminNewTeamForm.teamName || ""}
                                  onChange={(event) => updateAdminNewTeamField("teamName", event.target.value)}
                                  maxLength={160}
                                  placeholder="Team name"
                                />
                              </label>
                            </div>

                            <div className="purpleman-admin-edit-footer">
                              <span>{adminNewTeamMessage}</span>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={saveAdminNewTeam}
                                disabled={adminNewTeamSaving}
                              >
                                {adminNewTeamSaving ? "Saving..." : "Add Team"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {adminCreateTeam && (
                        <div className="purpleman-admin-edit-backdrop">
                          <div className="purpleman-admin-edit-panel" role="dialog" aria-modal="true" aria-labelledby="purpleman-admin-create-title">
                            <div className="purpleman-admin-edit-header">
                              <div>
                                <strong id="purpleman-admin-create-title">New User</strong>
                                <span>{adminCreateTeam}</span>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={closeAdminCreateUser}
                                disabled={adminCreateSaving}
                              >
                                Close
                              </button>
                            </div>

                            <div className="purpleman-admin-edit-grid">
                              <label>
                                <span>Username</span>
                                <input value={adminCreateForm.username || ""} onChange={(event) => updateAdminCreateField("username", event.target.value)} />
                              </label>
                              <label>
                                <span>Email</span>
                                <input type="email" value={adminCreateForm.email || ""} onChange={(event) => updateAdminCreateField("email", event.target.value)} />
                              </label>
                              <label>
                                <span>Phone</span>
                                <input value={adminCreateForm.phone || ""} onChange={(event) => updateAdminCreateField("phone", event.target.value)} />
                              </label>
                              <label>
                                <span>Password</span>
                                <div className="purpleman-admin-password-row">
                                  <input type="text" autoComplete="new-password" value={adminCreateForm.password || ""} onChange={(event) => updateAdminCreateField("password", event.target.value)} onFocus={(event) => event.target.select()} />
                                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => updateAdminCreateField("password", generateAdminPassword())}>
                                    Generate
                                  </button>
                                </div>
                              </label>
                              <label>
                                <span>Team</span>
                                <select value={adminCreateTeam} onChange={(event) => setAdminCreateTeam(event.target.value)}>
                                  {getAdminTeamOptions().map((teamOption) => (
                                    <option value={teamOption.Team} key={teamOption.Team}>{teamOption.Team_Name}</option>
                                  ))}
                                </select>
                              </label>
                            </div>

                            <div className="purpleman-admin-edit-footer">
                              <span>{adminCreateMessage}</span>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={saveNewAdminUser}
                                disabled={adminCreateSaving}
                              >
                                {adminCreateSaving ? "Saving..." : "Add User"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ), document.body)}

              <div className="d-flex gap-2 purpleman-control-row">
                <div className="col-12 text-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-purple w-100"
                    onClick={() => {
                      setShowWhiteBoardPreferences(true);
                      setWhiteBoardPreferencesMessage("");
                    }}
                  >
                    WB Preferences
                  </button>
                </div>
                <div className="flex-grow-1 text-start">
                  White Board Preferences
                </div>
              </div>

              {showWhiteBoardPreferences && typeof document !== "undefined" && createPortal((
                <div className="purpleman-whiteboard-preferences-backdrop">
                  <div className="purpleman-whiteboard-preferences" role="dialog" aria-modal="true" aria-labelledby="purpleman-wb-preferences-title">
                    <div className="purpleman-whiteboard-preferences__header">
                      <div>
                        <div className="purpleman-whiteboard-preferences__title" id="purpleman-wb-preferences-title">Manual Grade Colors</div>
                        <div className="purpleman-whiteboard-preferences__subtitle">
                          Team-wide colors used on White Board player cards.
                        </div>
                      </div>
                      <div className="purpleman-whiteboard-preferences__actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={resetWhiteBoardGradeColors}
                          disabled={whiteBoardPreferencesLoading || whiteBoardPreferencesSaving}
                        >
                          Defaults
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-purple"
                          onClick={saveWhiteBoardPreferences}
                          disabled={whiteBoardPreferencesLoading || whiteBoardPreferencesSaving}
                        >
                          {whiteBoardPreferencesSaving ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setShowWhiteBoardPreferences(false)}
                          disabled={whiteBoardPreferencesSaving}
                          aria-label="Close White Board preferences"
                        >
                          Close
                        </button>
                      </div>
                    </div>

                    <div className="purpleman-grade-color-section">
                      <div className="purpleman-grade-color-section__label">Numbers</div>
                      <div className="purpleman-grade-color-grid purpleman-grade-color-grid--numbers">
                        {WHITE_BOARD_NUMERIC_GRADE_KEYS.map((gradeKey) => {
                          const color = whiteBoardPreferences.gradeColors?.[gradeKey] || DEFAULT_WHITE_BOARD_GRADE_COLORS[gradeKey];
                          return (
                            <label className="purpleman-grade-color-control" key={`wb-grade-${gradeKey}`}>
                              <span
                                className="purpleman-grade-color-preview"
                                style={{
                                  backgroundColor: color,
                                  color: getReadableTextColor(color),
                                }}
                              >
                                {gradeKey}
                              </span>
                              <input
                                type="color"
                                value={color}
                                onChange={(event) => updateWhiteBoardGradeColor(gradeKey, event.target.value)}
                                disabled={whiteBoardPreferencesLoading || whiteBoardPreferencesSaving}
                                aria-label={`White Board grade ${gradeKey} color`}
                              />
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div className="purpleman-grade-color-section">
                      <div className="purpleman-grade-color-section__label">Letters</div>
                      <div className="purpleman-grade-color-grid">
                        {WHITE_BOARD_LETTER_GRADE_KEYS.map((gradeKey) => {
                          const color = whiteBoardPreferences.gradeColors?.[gradeKey] || DEFAULT_WHITE_BOARD_GRADE_COLORS[gradeKey];
                          return (
                            <label className="purpleman-grade-color-control" key={`wb-grade-${gradeKey}`}>
                              <span
                                className="purpleman-grade-color-preview"
                                style={{
                                  backgroundColor: color,
                                  color: getReadableTextColor(color),
                                }}
                              >
                                {gradeKey}
                              </span>
                              <input
                                type="color"
                                value={color}
                                onChange={(event) => updateWhiteBoardGradeColor(gradeKey, event.target.value)}
                                disabled={whiteBoardPreferencesLoading || whiteBoardPreferencesSaving}
                                aria-label={`White Board grade ${gradeKey} color`}
                              />
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {whiteBoardPreferencesMessage && (
                      <div className="purpleman-whiteboard-preferences__message">
                        {whiteBoardPreferencesMessage}
                      </div>
                    )}
                  </div>
                </div>
              ), document.body)}

              {isAdmin && (
                <div className="purpleman-admin-alerts">
                  <div className="text-start fs-8 purpleman-admin-alerts__count">
                    Pending Alert Messages: {pendingAlertError || (pendingAlertCount === null ? "..." : pendingAlertCount)}
                  </div>
                  <div className="purpleman-alert-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-purple w-100"
                      onClick={sendAlertMessages}
                      disabled={sendingAlerts}
                    >
                      {sendingAlerts ? "Sending..." : "Send Alert Messages"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success w-100"
                      onClick={confirmAlertTransactions}
                      disabled={confirmingTransactions || sendingAlerts}
                    >
                      {confirmingTransactions ? "Refreshing..." : "Confirm Transactions"}
                    </button>
                  </div>
                  {sendAlertStatus && <div className="mt-1 text-start fs-8">{sendAlertStatus}</div>}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid rgba(0,0,0,0.08)", padding: 8 }}>
          <div className="d-flex align-items-center justify-content-center p-2 gap-3">
            <div className="col-16">
              <button className="btn btn-outline-secondary w-100 fs-6" onClick={handleClose}>
                Cancel / Close
              </button>
            </div>
            <div className="col-16">
              <button className="btn btn-outline-danger w-100 fs-6" onClick={signout}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    </NotDragable>
  );
};

export default PurpleManBox;
